import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Chip,
  IconButton,
  Button,
  Tooltip,
  InputAdornment,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoIcon from '@mui/icons-material/Info';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';

const OMOP_SUGGESTIONS_TABLE_COPY = {
  de: {
    noSuggestions: 'Keine KI-Vorschläge verfügbar',
    suggestionsCount: '{{filtered}} von {{total}} Vorschlägen',
    savedCount: '{{count}} gespeichert',
    resetFilter: 'Filter zurücksetzen',
    rank: 'Rang',
    conceptName: 'Concept Name',
    conceptId: 'Concept ID',
    vocabulary: 'Vocabulary',
    domain: 'Domain',
    score: 'Score',
    pinColumn: 'Pin',
    action: 'Aktion',
    current: 'Aktuell',
    confidence: 'KI-Konfidenz: {{percent}}%',
    openInAthena: 'In ATHENA anzeigen',
    pin: 'Vorschlag speichern',
    unpin: 'Nicht mehr speichern',
    inUse: 'In Verwendung',
    propose: 'Vorschlagen',
    proposalTitle: 'Vorschlag: {{name}}',
    proposalHelp: '{{vocabulary}} {{code}} als alternatives Mapping vorschlagen',
    proposalPlaceholder: 'Begründung (optional): Warum ist dieses Concept besser geeignet?',
    submit: 'Einreichen',
    cancel: 'Abbrechen',
    suggestionReason: '💡 Warum dieser Vorschlag?',
    noResults: 'Keine Ergebnisse für "{{query}}"',
  },
  en: {
    noSuggestions: 'No AI suggestions available',
    suggestionsCount: '{{filtered}} of {{total}} suggestions',
    savedCount: '{{count}} saved',
    resetFilter: 'Reset filter',
    rank: 'Rank',
    conceptName: 'Concept name',
    conceptId: 'Concept ID',
    vocabulary: 'Vocabulary',
    domain: 'Domain',
    score: 'Score',
    pinColumn: 'Pin',
    action: 'Action',
    current: 'Current',
    confidence: 'AI confidence: {{percent}}%',
    openInAthena: 'Open in ATHENA',
    pin: 'Save suggestion',
    unpin: 'Remove saved suggestion',
    inUse: 'In use',
    propose: 'Propose',
    proposalTitle: 'Proposal: {{name}}',
    proposalHelp: 'Suggest {{vocabulary}} {{code}} as an alternative mapping',
    proposalPlaceholder: 'Justification (optional): Why is this concept more suitable?',
    submit: 'Submit',
    cancel: 'Cancel',
    suggestionReason: '💡 Why this suggestion?',
    noResults: 'No results for "{{query}}"',
  },
  pt: {
    noSuggestions: 'Nenhuma sugestão de IA disponível',
    suggestionsCount: '{{filtered}} de {{total}} sugestões',
    savedCount: '{{count}} guardados',
    resetFilter: 'Repor filtro',
    rank: 'Posição',
    conceptName: 'Nome do conceito',
    conceptId: 'Concept ID',
    vocabulary: 'Vocabulário',
    domain: 'Domínio',
    score: 'Pontuação',
    pinColumn: 'Pin',
    action: 'Ação',
    current: 'Atual',
    confidence: 'Confiança da IA: {{percent}}%',
    openInAthena: 'Abrir no ATHENA',
    pin: 'Guardar sugestão',
    unpin: 'Remover sugestão guardada',
    inUse: 'Em uso',
    propose: 'Propor',
    proposalTitle: 'Proposta: {{name}}',
    proposalHelp: 'Propor {{vocabulary}} {{code}} como mapeamento alternativo',
    proposalPlaceholder: 'Justificação (opcional): Porque é que este conceito é mais adequado?',
    submit: 'Submeter',
    cancel: 'Cancelar',
    suggestionReason: '💡 Porque esta sugestão?',
    noResults: 'Nenhum resultado para "{{query}}"',
  },
};

/**
 * OmopSuggestionsTable Component
 * Displays AI-generated OMOP concept suggestions in a sortable, searchable table
 * Allows users to pin suggestions and submit change proposals inline
 *
 * @param {object} props
 * @param {array} props.candidates - Array of AI-generated concept candidates
 * @param {string} props.currentTopMatchId - Concept ID of currently selected top match
 * @param {array} props.pinnedConceptIds - Array of user-pinned concept IDs
 * @param {function} props.onSelectCandidate - Callback when user selects a candidate (direct change for Data Stewards)
 * @param {function} props.onSubmitProposal - Callback when user submits a change proposal ({ candidate, justification })
 * @param {function} props.onTogglePin - Callback when user pins/unpins a candidate
 * @param {boolean} props.isProcessing - Whether an action is in progress
 * @param {string} props.searchQuery - External search query (optional)
 * @param {function} props.onSearchChange - Callback when search changes (optional)
 */
const OmopSuggestionsTable = ({
  candidates = [],
  currentTopMatchId,
  pinnedConceptIds = [],
  onSelectCandidate,
  onSubmitProposal,
  onTogglePin,
  isProcessing = false,
  searchQuery: externalSearchQuery,
  onSearchChange
}) => {
  const { i18n } = useTranslation();
  // State for table controls
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : internalSearchQuery;
  const [orderBy, setOrderBy] = useState('score');
  const [order, setOrder] = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);

  // State for proposal form
  const [expandedProposalRow, setExpandedProposalRow] = useState(null);
  const [proposalJustification, setProposalJustification] = useState('');
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = OMOP_SUGGESTIONS_TABLE_COPY[language];

  const handleSearchChange = (value) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchQuery(value);
    }
  };

  // Filter candidates based on search query
  const filteredCandidates = useMemo(() => {
    if (!searchQuery.trim()) return candidates;

    const query = searchQuery.toLowerCase();
    return candidates.filter(candidate =>
      candidate.conceptName?.toLowerCase().includes(query) ||
      candidate.conceptId?.toString().includes(query) ||
      candidate.conceptCode?.toLowerCase().includes(query) ||
      candidate.vocabulary?.toLowerCase().includes(query)
    );
  }, [candidates, searchQuery]);

  // Sort candidates
  const sortedCandidates = useMemo(() => {
    const sorted = [...filteredCandidates];

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'rank':
          aValue = a.rank || 999;
          bValue = b.rank || 999;
          break;
        case 'score':
          aValue = a.score || 0;
          bValue = b.score || 0;
          break;
        case 'name':
          aValue = a.conceptName || '';
          bValue = b.conceptName || '';
          break;
        case 'vocabulary':
          aValue = a.vocabulary || '';
          bValue = b.vocabulary || '';
          break;
        case 'domain':
          aValue = a.domain || '';
          bValue = b.domain || '';
          break;
        default:
          return 0;
      }

      // Handle string vs number comparison
      if (typeof aValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return order === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }
    });

    return sorted;
  }, [filteredCandidates, orderBy, order]);

  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle row expand
  const handleExpandRow = (conceptId) => {
    setExpandedRow(expandedRow === conceptId ? null : conceptId);
  };

  // Check if concept is pinned
  const isPinned = (conceptId) => {
    const lookupId = String(conceptId ?? '');
    return pinnedConceptIds.some(id => String(id ?? '') === lookupId);
  };

  // Check if concept is current top match
  const isCurrentTopMatch = (conceptId) => {
    return String(conceptId ?? '') === String(currentTopMatchId ?? '');
  };

  // Get score color
  const getScoreColor = (score) => {
    if (score >= 0.9) return 'success.main';
    if (score >= 0.7) return 'warning.main';
    return 'text.secondary';
  };

  // Handle proposal form open/close
  const handleOpenProposalForm = (conceptId) => {
    setExpandedProposalRow(conceptId);
    setProposalJustification('');
  };

  const handleCloseProposalForm = () => {
    setExpandedProposalRow(null);
    setProposalJustification('');
  };

  // Handle proposal submission
  const handleSubmitProposal = async (candidate) => {
    if (onSubmitProposal) {
      const result = await onSubmitProposal({
        candidate,
        justification: proposalJustification
      });

      if (result === false || result === null) {
        return;
      }
    }

    handleCloseProposalForm();
  };

  if (candidates.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          {copy.noSuggestions}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Results Count */}
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          {copy.suggestionsCount
            .replace('{{filtered}}', filteredCandidates.length)
            .replace('{{total}}', candidates.length)}
          {pinnedConceptIds.length > 0 && ` • ${copy.savedCount.replace('{{count}}', pinnedConceptIds.length)}`}
        </Typography>
        {searchQuery && (
          <Button size="small" onClick={() => handleSearchChange('')}>
            {copy.resetFilter}
          </Button>
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell width={40} />
              <TableCell width={60}>
                <TableSortLabel
                  active={orderBy === 'rank'}
                  direction={orderBy === 'rank' ? order : 'asc'}
                  onClick={() => handleRequestSort('rank')}
                >
                  {copy.rank}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  {copy.conceptName}
                </TableSortLabel>
              </TableCell>
              <TableCell width={120}>{copy.conceptId}</TableCell>
              <TableCell width={100}>
                <TableSortLabel
                  active={orderBy === 'vocabulary'}
                  direction={orderBy === 'vocabulary' ? order : 'asc'}
                  onClick={() => handleRequestSort('vocabulary')}
                >
                  {copy.vocabulary}
                </TableSortLabel>
              </TableCell>
              <TableCell width={120}>
                <TableSortLabel
                  active={orderBy === 'domain'}
                  direction={orderBy === 'domain' ? order : 'asc'}
                  onClick={() => handleRequestSort('domain')}
                >
                  {copy.domain}
                </TableSortLabel>
              </TableCell>
              <TableCell width={80} align="center">
                <TableSortLabel
                  active={orderBy === 'score'}
                  direction={orderBy === 'score' ? order : 'asc'}
                  onClick={() => handleRequestSort('score')}
                >
                  {copy.score}
                </TableSortLabel>
              </TableCell>
              <TableCell width={50} align="center">
                <Tooltip title="ATHENA">
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    borderRadius: 1,
                    p: 0.5,
                    width: 24,
                    height: 24
                  }}>
                    <Box
                      component="img"
                      src="/athena-favicon.ico"
                      alt="ATHENA"
                      sx={{ width: 14, height: 14 }}
                    />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell width={60} align="center">{copy.pinColumn}</TableCell>
              <TableCell width={120} align="center">{copy.action}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCandidates.map((candidate, idx) => {
              const isTop = isCurrentTopMatch(candidate.conceptId);
              const pinned = isPinned(candidate.conceptId);
              const isExpanded = expandedRow === candidate.conceptId;
              const hasReason = candidate.reason && candidate.reason.trim().length > 0;

              return (
                <React.Fragment key={candidate.conceptId || idx}>
                  <TableRow
                    sx={{
                      backgroundColor: isTop ? '#e8f5e9' : pinned ? '#fff3e0' : 'inherit',
                      '&:hover': {
                        backgroundColor: isTop ? '#c8e6c9' : pinned ? '#ffe0b2' : '#f5f5f5'
                      }
                    }}
                  >
                    {/* Expand Button */}
                    <TableCell>
                      {hasReason && (
                        <IconButton
                          size="small"
                          onClick={() => handleExpandRow(candidate.conceptId)}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      )}
                    </TableCell>

                    {/* Rank */}
                    <TableCell>
                      <Chip
                        label={`#${candidate.rank || (idx + 1)}`}
                        size="small"
                        color={isTop ? "success" : "default"}
                        variant={isTop ? "filled" : "outlined"}
                      />
                    </TableCell>

                    {/* Concept Name */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={isTop ? "bold" : "regular"}>
                          {candidate.conceptName}
                        </Typography>
                        {isTop && (
                          <Chip label={copy.current} size="small" color="success" sx={{ height: 20 }} />
                        )}
                      </Box>
                    </TableCell>

                    {/* Concept ID */}
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {candidate.conceptId}
                      </Typography>
                    </TableCell>

                    {/* Vocabulary */}
                    <TableCell>
                      <Chip label={candidate.vocabulary} size="small" variant="outlined" />
                    </TableCell>

                    {/* Domain */}
                    <TableCell>
                      <Typography variant="body2">
                        {candidate.domain || 'Measurement'}
                      </Typography>
                    </TableCell>

                    {/* Score */}
                    <TableCell align="center">
                      <Tooltip title={copy.confidence.replace('{{percent}}', Math.round((candidate.score || 0) * 100))}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ color: getScoreColor(candidate.score || 0) }}
                        >
                          {Math.round((candidate.score || 0) * 100)}%
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* ATHENA Link */}
                    <TableCell align="center">
                      <Tooltip title={copy.openInAthena}>
                        <IconButton
                          size="small"
                          component="a"
                          href={`https://athena.ohdsi.org/search-terms/terms/${candidate.conceptId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>

                    {/* Pin Button */}
                    <TableCell align="center">
                      <Tooltip title={pinned ? copy.unpin : copy.pin}>
                        <IconButton
                          size="small"
                          onClick={() => onTogglePin && onTogglePin(candidate)}
                          disabled={isProcessing}
                          color={pinned ? "primary" : "default"}
                        >
                          {pinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>

                    {/* Action Button */}
                    <TableCell align="center">
                      {isTop ? (
                        <Chip
                          label={copy.inUse}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => handleOpenProposalForm(candidate.conceptId)}
                          disabled={isProcessing}
                        >
                          {copy.propose}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row - Proposal Form */}
                  {expandedProposalRow === candidate.conceptId && (
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                        <Collapse in={true} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 3, backgroundColor: '#f9f9f9', border: '2px solid #1976d2', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {copy.proposalTitle.replace('{{name}}', candidate.conceptName)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                              {copy.proposalHelp
                                .replace('{{vocabulary}}', candidate.vocabulary)
                                .replace('{{code}}', candidate.conceptCode)}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                              {/* Justification TextField */}
                              <TextField
                                size="small"
                                placeholder={copy.proposalPlaceholder}
                                value={proposalJustification}
                                onChange={(e) => setProposalJustification(e.target.value)}
                                multiline
                                rows={2}
                                sx={{ flexGrow: 1 }}
                              />

                              {/* Action Buttons */}
                              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => void handleSubmitProposal(candidate)}
                                >
                                  {copy.submit}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={handleCloseProposalForm}
                                >
                                  {copy.cancel}
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Expanded Row - Show Reason */}
                  {hasReason && (
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, pl: 6, pr: 2, backgroundColor: '#fafafa' }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                              <InfoIcon fontSize="small" color="primary" sx={{ mt: 0.3 }} />
                              <Box>
                                <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                                  {copy.suggestionReason}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {candidate.reason}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredCandidates.length === 0 && (
        <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            {copy.noResults.replace('{{query}}', searchQuery)}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default OmopSuggestionsTable;
